<template>
  <div
    class="gmfover_wrapper"
    :class="[
      { gmfover_minimized: minimized },
      { gmfover_horizontal: isHorizontal },
      { gmfover_custsol: customSolution },
      { gmfover_smalltext: Object.keys(l_gmfNameRest).length > 3 },
    ]"
    data-tst="gmf_selector_tile"
    @click="e => $emit('click', e)"
  >
    <div class="gmfover text-noselect gw_loading_text">
      <div class="gmfover_inner">
        <div class="gmfover_content">
          <div
            v-if="customSolution || gameformat.group"
            class="gmfover_title"
          >
            <div
              class="gmfover_title_text text-nowrap"
              data-tst="gmf_gametype_name"
              v-html="
                customSolution ? customSolution.title : $t(`gametypes.groups.${gameformat.group}`)
              "
            />

            <div
              class="gmfover_title_text_depth"
              v-html="l_stackDepth"
            ></div>
          </div>
          <slot name="detail">
            <div
              v-for="(text, key) in minimized ? {} : l_gmfNameRest"
              :key="key"
              class="gmfover_content_info"
              :class="key"
            >
              <div class="gmfover_item">
                <span class="circle-bullet"></span>
                <span
                  class="gmfover_item_text"
                  :class="text.length"
                  v-html="text"
                />
              </div>
            </div>

            <div
              v-if="!minimized && hasChangeBtn"
              data-tst="gmf_selector_opener"
              class="gw_btn text-medium weight_500 mt-auto clr-greys clr-hover"
              style="margin-bottom: 4px; --clr-back: rgba(255, 255, 255, 0.1)"
            >
              <div class="mdi mdi-cog mr-1"></div>
              {{ $t('btn.change') }}
            </div>
          </slot>
        </div>

        <div
          v-if="!minimized && btnOptions.length"
          class="gmf_editmenu gmf_editmenu_on-hovered clr-grey"
        >
          <div
            v-for="(item, idx) in btnOptions"
            :key="idx"
            class="icon_btn mdi clr-hover csst csst_nm"
            style="font-size: 1.125rem; padding: 0.25rem"
            :class="item.icon"
            v-bind="item.props || {}"
            :data-tst="item.id"
            @click.stop="e => item.fn(e)"
            @mouseenter="e => fl_positionTooltip(e, { placement: 'right' })"
          >
            <div
              class="csst_content clr-white"
              style="position: fixed"
              v-html="item.text"
            ></div>
          </div>
        </div>

        <GwMenu
          v-else-if="btnOptions.length"
          ref="gwmenu"
          :isInside="true"
          class="gmf_editmenu_menu gmf_editmenu_on-hovered"
          @click.stop
        >
          <template #opener>
            <div class="f-center h-100">
              <i
                class="mdi icon_btn clr-hover mdi mdi-dots-vertical"
                style="font-size: 1.25rem; padding: 0.25rem"
              />
            </div>
          </template>
          <template #default="{ methods }">
            <GwBtns
              :options="btnOptions"
              class="gwbtns_vertical_white gw_menu"
              @click="
                (e, item) => {
                  methods.toggleOpen(false)
                  item.fn(e)
                }
              "
            >
              <template #default="{ item }">
                <div
                  v-bind="item.props || {}"
                  class="f-center clr-white"
                >
                  <i
                    class="mdi text-normal mr-2"
                    :class="item.icon"
                  ></i>
                  <div v-html="item.text"></div>
                </div>
              </template>
            </GwBtns>
          </template>
        </GwMenu>
      </div>
    </div>

    <slot name="after"></slot>
    <slot name="extra"></slot>
  </div>
</template>

<script>
import GwMenu from '@/components/lib/GwMenu'
import GwBtns from '@/components/lib/GwBtns'
import { getGmfNameConf } from '@/store/gameformat/gameformat'
import float_mix from '@/mixins/float_mix'
import { omit } from 'lodash'

export default {
  name: 'GameFormatOptionVertical',
  components: {
    GwMenu,
    GwBtns,
  },
  mixins: [float_mix],
  props: {
    minimized: Boolean,
    hasBubble: Boolean,
    hasTree: Boolean,
    isHorizontal: Boolean,
    hasStack: { type: Boolean, default: true },
    hasDepth: { type: Boolean, default: true },
    hasChangeBtn: { type: Boolean, default: true },
    gameformat: Object,
    gmfName: Object,
    customSolution: Object,
  },
  emits: ['click', 'click:tree', 'click:editsolution', 'click:bubble'],
  data() {
    return {}
  },
  methods: {
    gmix_numRoundTo5bb(num) {
      if (typeof num === 'number') {
        return Math.round(num * 2) / 2
      }
      return num
    },
    gmix_getStacksDisplayname(stacks) {
      const res = stacks
        .split('-')
        .map(n => `<span class="text-nowrap">${this.gmix_numRoundTo5bb(Number(n))}</span>`)
        .join('-')
      return res
    },
    getLocalGmfName() {
      const nameConf = getGmfNameConf({ gameformat: this.gameformat })
      delete nameConf.cash69max_type
      return omit(nameConf, 'group')
    },
  },
  computed: {
    l_gmfName() {
      return this.customSolution ? this.customSolution.details : this.getLocalGmfName()
    },
    l_stackDepth() {
      return this.customSolution
        ? ''
        : this.l_gmfName.depth || this.l_gmfName.stacks || this.l_gmfName.avg_stacks
    },
    l_gmfNameRest() {
      if (this.customSolution) {
        return this.customSolution.details
      }
      const res = this.getLocalGmfName()
      delete res.depth
      delete res.stacks
      delete res.avg_stacks
      if (res.rake) {
        res.format = `${res.format} ${res.rake}`
        delete res.rake
      }
      return res
    },
    btnOptions() {
      return [
        {
          icon: 'mdi-pencil',
          id: 'gmfv_edit',
          text: this.$t(`tooltips.edit_solution`),
          fn: e => {
            this.$emit('click:editsolution', e)
          },
          hide: !this.hasTree,
        },
        {
          icon: 'mdi-information-outline',
          id: 'node_lock',
          text: this.$t(`tooltips.show_info`),
          fn: e => {
            this.$emit('click:tree', e)
          },
          props: { 'data-gwmenu': 'noclose' },
          hide: !this.hasTree,
        },
        {
          icon: 'mdi-chart-bubble',
          id: 'compare',
          text: this.$t(`common.bubble_factor`),
          fn: e => {
            this.$emit('click:bubble', e)
          },
          hide: !this.hasBubble,
        },
      ].filter(i => !i.hide)
    },
  },
}
</script>

<style lang="scss">
@import '@/styles/mixins.scss';

.gmf_editmenu {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  border-radius: 0 4px 4px 0;
  gap: 0.25rem;
  padding: 0.25rem 0;
  right: -2px;
  position: relative;
  height: 100%;
  width: calc(100% + 2px);
  width: 2rem;
  &:before {
    content: ' ';
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    top: -2px;
    right: 0px;
    height: calc(100% + 4px);
    width: 100%;
    border-radius: 0 4px 4px 0;
    background: rgba(0, 0, 0, 0.18);
  }
}

.gmf_editmenu_menu {
  padding: 0 0.125rem 0 0;
}

.gmfover {
  transition: background 0.22s;
  &_wrapper {
    --width: 9.625em;
    --height: 8.75em;
    --height-min: 2.25em;

    height: var(--height);
    min-width: var(--width);
    gap: 0.25em;
    display: flex;
    flex-direction: column;
    --clr-card-primary: var(--clr-hero);

    --clr-card-back: var(--clr-black-1);
    --clr-card-back-hover: var(--clr-black-1-2);
    --clr-card-back-active: var(--clr-black-1-2);

    --clr-title: var(--clr-grey-2);
    --clr-title-hover: var(--clr-card-primary);
    --clr-title-active: var(--clr-card-primary);

    --border-width: 2px;
    --clr-border: var(--clr-card-back);
    --clr-border-hover: var(--clr-card-primary);

    --clr-title: inherit;
    --opener-height: calc((100% / 4) - 0.625em);
    --clr-card-back: var(--clr-black-1);

    --size-title: 0.875em;
    --size-item: 1em;

    color: var(--clr-grey-2);
  }
  &_smalltext {
    --size-item: 0.875em;
  }
  flex: 1;

  border-radius: 4px;
  position: relative;
  display: flex;
  flex-direction: column;
  background-clip: padding-box;
  background: var(--clr-card-back);
  width: 100%;
  padding: 2px 2px 2px calc(0.8em + 2px);

  .gmf-btn-detail {
    margin: 4px 0 -2px 0;
    align-items: center;
    justify-content: flex-start;
    font-size: 0.875em;
    z-index: 1;
    position: relative;
  }
  &_inner {
    width: 100%;
    border-radius: 4px;

    display: flex;
    flex: 1;
    z-index: 2;
    position: relative;
  }
  &_content {
    display: flex;
    flex-direction: column;
    flex: 1;
    padding: 0 0.8em 0 0;
  }
  &_stacks-opener {
    --opener-radius: 0px 0px 4px 4px;
    .csst_content {
      top: -1.5em;
    }
  }
  &_custsol {
    &:not(.gmfover_minimized) {
      min-width: 9rem;
    }
  }
  &_horizontal {
    height: auto;
    width: 100%;
    .gmfover_content {
      .gmfover_title {
        width: 100%;
      }
      padding-right: 2em;
      display: flex;
      flex-direction: row;
      align-items: center;
      flex-wrap: wrap;
      gap: 0.5em;
    }
  }
  &_minimized {
    --height: var(--height-min);
    --width: auto;
    gap: 0;

    flex-direction: row;
    padding: 0;
    .gmfover {
      padding: 0;
    }
    .gmfover_title {
      padding: 0 0.5em 0 0.5em;
      gap: 0.5rem;
      line-height: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      &_text {
        padding: 0;
      }
    }
    .gmfover_inner {
      min-width: 0;
      display: flex;
      padding: 0;
      // gap: 1em;
      align-items: center;
      justify-content: space-between;
    }
    .gmfover_content {
      justify-content: center;
      padding: 0;
    }
    .gmf-btn-detail {
      margin: 0 0px 0 4px;
      padding: 0 0.5em;
      font-size: 1em;
      border-radius: 6px;
      height: 100%;
      background: var(--clr-card-back);
    }
  }
  &_title {
    display: flex;
    align-items: center;
    font-size: var(--size-title);
    padding: 0.25em 0 0.25em 0;
    font-weight: 700;
    color: var(--clr-title);
    position: relative;
    width: 100%;
    &_text {
      padding-right: 0.5em;
    }
    .cog-icon {
      margin-left: auto;
      line-height: 1;
      font-size: 0.875em;
      color: var(--clr-title);
    }
  }
  &_item {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 0.5em;
    max-width: 12em;
    position: relative;
    word-break: break-all;
    font-weight: 500;
    font-size: var(--size-item);
    &_text {
      white-space: normal;
      word-break: break-word;
    }
  }
}

@media (hover: hover) and (pointer: fine) {
  .gmfover {
    &:hover {
      cursor: pointer;
      background: var(--clr-black-1-3);
    }
  }
}
</style>
